// The "commons" card — rendered on the You tab (always) and the Now tab (calm-times
// only; suppression decided by the caller). Per-period-first run-rate, cumulative with
// one-time segregated, and a personal mirror scoped to periods since the local account's
// creation. Cost is labelled "operator-stated", never as fact (master §6).
import { button, hairlineRow } from "../../design/components.js";
import type { CommonsState } from "../../commons/feed.js";
import {
  chippedInThisPeriod, commonsTotals, periodSeries, yourCostSoFar, yourGivenSoFar,
} from "../../commons/mirror.js";

export type CommonsCardCtx = {
  t: (k: string, p?: Record<string, string | number>) => string;
  state: CommonsState;
  myDid: string;
  createdAtMicros: number;
  nowMicros: number;
  onChipIn: () => void;
  onTransparency: () => void;
  hostCurrency: string;
};

function elx(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function money(v: number, cur: string, approx = false): string {
  return `${approx ? "≈ " : ""}${Math.round(v)} ${cur}`;
}

export function commonsCard(ctx: CommonsCardCtx): HTMLElement {
  const { t, state, hostCurrency } = ctx;
  const root = elx("section", "you-card commons-card");
  root.append(elx("div", "you-card__title bx-microlabel", t("commons.card.title")));

  if (state.costs.length === 0 && state.contribs.length === 0) {
    root.append(elx("p", "you-note bx-body", t("commons.card.empty")));
    root.append(chipInRow(ctx));
    return root;
  }

  const approx = state.contribs.some((c) => c.hostValueApprox);
  const series = periodSeries(state.costs, state.contribs);

  // Primary — this period (latest) recurring run-rate.
  if (series.length > 0) {
    const latest = series[series.length - 1];
    root.append(elx("div", "commons-card__section bx-microlabel", t("commons.card.thisPeriod")));
    root.append(hairlineRow({
      leading: t("commons.card.runRate"),
      trailing: `${money(latest.contributed, hostCurrency, approx)} / ${money(latest.cost, hostCurrency)}`,
    }));
    root.append(hairlineRow({
      leading: latest.net >= 0 ? t("commons.card.surplus") : t("commons.card.deficit"),
      trailing: money(Math.abs(latest.net), hostCurrency, approx),
      tone: latest.net >= 0 ? "aid" : "accent",
    }));
  }

  // Secondary — cumulative, one-time on its own line.
  const totals = commonsTotals(state.costs, state.contribs);
  root.append(elx("div", "commons-card__section bx-microlabel", t("commons.card.cumulative")));
  root.append(hairlineRow({
    leading: t("commons.card.runRate"),
    trailing: `${money(totals.totalContributed, hostCurrency, approx)} / ${money(totals.totalSpend, hostCurrency)}`,
  }));
  if (totals.totalOneTime > 0) {
    root.append(hairlineRow({ leading: t("commons.card.oneTime"), trailing: money(totals.totalOneTime, hostCurrency) }));
  }

  // Personal mirror — scoped to periods on/after this account's creation.
  root.append(hairlineRow({
    leading: t("commons.card.yourCost"),
    trailing: money(yourCostSoFar(state.costs, ctx.createdAtMicros), hostCurrency),
  }));
  root.append(hairlineRow({
    leading: t("commons.card.yourGiven"),
    trailing: money(yourGivenSoFar(state.contribs, ctx.myDid), hostCurrency, approx),
  }));

  // Cost is a claim, not fact.
  root.append(elx("p", "you-note bx-microlabel", t("commons.card.operatorStated")));

  root.append(chipInRow(ctx));
  return root;
}

/** Compact commons surface for the Now tab: one short line of context + a single,
 *  content-width button — not the full You-tab card. If I've chipped in this funding
 *  period it thanks me and links to transparency; otherwise it states what running
 *  Beacon cost this month and opens the chip-in dialog. Keeps the calm Now tab light. */
export function commonsChipInButton(ctx: CommonsCardCtx): HTMLElement {
  const { t, state, hostCurrency } = ctx;
  const row = elx("div", "commons-chip-compact");
  const chipped = chippedInThisPeriod(state.costs, state.contribs, ctx.myDid);
  if (chipped) {
    row.append(elx("p", "commons-chip-compact__line bx-body", t("commons.card.chippedIn")));
    const btn = button({ label: t("commons.card.viewTransparency"), variant: "secondary", onClick: ctx.onTransparency });
    btn.setAttribute("data-action", "commons-transparency");
    row.append(btn);
    return row;
  }
  // What running Beacon cost this period — the "why chip in" framing, tied to alerting.
  const series = periodSeries(state.costs, state.contribs);
  const need = series.length > 0 ? series[series.length - 1].cost : 0;
  if (need > 0) {
    row.append(elx("p", "commons-chip-compact__line bx-body",
      t("commons.card.need", { amount: money(need, hostCurrency) })));
  }
  const btn = button({ label: t("commons.card.chipIn"), variant: "primary", onClick: ctx.onChipIn });
  btn.setAttribute("data-action", "commons-chip-in");
  row.append(btn);
  return row;
}

function chipInRow(ctx: CommonsCardCtx): HTMLElement {
  const row = elx("div", "commons-card__actions");
  const chip = button({ label: ctx.t("commons.card.chipIn"), variant: "primary", onClick: ctx.onChipIn });
  chip.setAttribute("data-action", "commons-chip-in");
  const trans = button({ label: ctx.t("commons.card.viewTransparency"), variant: "secondary", onClick: ctx.onTransparency });
  trans.setAttribute("data-action", "commons-transparency");
  row.append(chip, trans);
  return row;
}
